import { useEffect, useMemo, useState } from "react";
import { MDBBtn, MDBCard, MDBCardBody, MDBCardTitle, MDBIcon, MDBTypography, MDBBtnGroup, MDBSpinner } from "mdb-react-ui-kit";
import { useTranslation } from "react-i18next";
import { generateClient } from 'aws-amplify/api';
import { captureError } from '../../../../utils/sentryHelpers';
import { isoToGermanDate } from "../../../../utils/dateTools";
import shortUUID from 'short-uuid';
import { PDFDownloadLink } from '@react-pdf/renderer';
import MobileTicketPDF from '../../../../components-user/tickets/MobileTicketPDF';
import { getLocalizedText } from "../../../../utils/localizedText";
import QRCode from 'qrcode';

const client = generateClient();

export default function Success({ ticketId, sessionId, orderId, event }) {
  const { t, i18n } = useTranslation();

  // Simplified state management
  const [ticket, setTicket] = useState(null);
  const [isLoadingTicket, setIsLoadingTicket] = useState(true); // Loading ticket data
  const [isPDFReady, setIsPDFReady] = useState(false); // PDF generation complete
  const [showDownload, setShowDownload] = useState(false); // Show download buttons (after 10s delay)
  const [error, setError] = useState(null);

  // QR code for mobile view
  const [qrImageUrl, setQrImageUrl] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Payment verification state
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);

  // Invoice state
  const [invoiceData, setInvoiceData] = useState(null);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);

  // Fetch ticket data
  const fetchTicket = async (ticketIdToFetch) => {
    try {
      const ticketData = await client.graphql({
        query: /* GraphQL */ `
          query GetTicket($id: ID!) {
            getTicket(id: $id) {
              id
              type
              eventDays
              createdAt
              paymentStatus
              ticketFile {
                s3Path
              }
              personalAddress {
                title
                firstName
                lastName
                company
              }
              personalContact {
                phone
                email
              }
              event {
                id
                name {
                  language
                  text
                }
                type
                startDate
                endDate
                image {
                  fileName
                }
              }
            }
          }
        `,
        variables: { id: ticketIdToFetch }
      });

      return ticketData.data.getTicket;
    } catch (error) {
      captureError(error, {
        feature: 'ticket-management',
        operation: 'fetch-ticket-details',
        consoleMessage: 'Error fetching ticket:',
        context: { ticketId: ticketIdToFetch }
      });
      throw error;
    }
  };

  // Fetch invoice from OrderFinancial
  const fetchInvoice = async (orderIdToFetch) => {
    try {
      const orderData = await client.graphql({
        query: /* GraphQL */ `
          query GetOrder($id: ID!) {
            getOrder(id: $id) {
              id
              orderFinancialId
            }
          }
        `,
        variables: { id: orderIdToFetch }
      });

      const financialId = orderData.data.getOrder?.orderFinancialId;
      if (!financialId) return null;

      const financialData = await client.graphql({
        query: /* GraphQL */ `
          query GetOrderFinancial($id: ID!) {
            getOrderFinancial(id: $id) {
              id
              documents {
                documentType
                s3Path
                documentNumber
                documentName
                status
              }
            }
          }
        `,
        variables: { id: financialId }
      });

      const documents = financialData.data.getOrderFinancial?.documents || [];
      const invoice = documents.find(doc => doc.documentType === "invoice" && doc.s3Path && doc.status === "active");
      return invoice || null;
    } catch (error) {
      captureError(error, {
        feature: 'ticket-management',
        operation: 'fetch-invoice',
        consoleMessage: 'Error fetching invoice:',
        context: { orderId: orderIdToFetch }
      });
      return null;
    }
  };

  // Non-Stripe flow: user placed an order without going through Stripe.
  // paymentStatus stays "pending" until accounting marks it paid manually,
  // so we must not block the success page on it.
  const isNonStripeFlow = useMemo(
    () => !sessionId && event?.instantPaymentOnly !== true && !!orderId,
    [sessionId, event?.instantPaymentOnly, orderId]
  );

  // Invoice PDF is only auto-generated when the event opts in via
  // autoGenerateInvoice. For non-Stripe events without that flag, accounting
  // handles the invoice manually and there is nothing to poll for.
  const skipInvoicePolling = useMemo(
    () => isNonStripeFlow && event?.autoGenerateInvoice !== true,
    [isNonStripeFlow, event?.autoGenerateInvoice]
  );

  // Single polling effect for ticket — checks both paymentStatus and PDF readiness
  useEffect(() => {
    // We need either ticketId or sessionId to proceed
    if (!ticketId && !sessionId) {
      setError(t("No ticket or session ID provided"));
      setIsLoadingTicket(false);
      return;
    }

    let isMounted = true;
    let pollingTimeout = null;
    let attempts = 0;
    const maxPaymentAttempts = 20; // ~60 seconds at 3s intervals

    const pollTicket = async (currentTicketId) => {
      if (!isMounted) return;
      attempts++;

      try {
        const ticketData = await fetchTicket(currentTicketId);

        if (!isMounted) return;

        // Update ticket state
        setTicket(ticketData);

        const status = ticketData?.paymentStatus;
        const isPaid = status === "paid";
        const isFree = !orderId; // Free tickets have no order
        const isExpiredOrFailed = status === "expired" || status === "failed";

        // Check payment status
        if (isPaid || isFree || isNonStripeFlow) {
          setPaymentVerified(true);
        } else if (isExpiredOrFailed || (attempts >= maxPaymentAttempts && !isPaid && !isFree)) {
          // Payment failed or timed out — show error, but keep ticket and order in DB
          setPaymentFailed(true);
          setIsLoadingTicket(false);
          return; // Stop polling
        }

        // Check if PDF is ready (only matters if payment is verified)
        if ((isPaid || isFree || isNonStripeFlow) && ticketData?.ticketFile?.s3Path) {
          setIsLoadingTicket(false);
          setIsPDFReady(true);

          // Handle 10-second delay for download buttons
          const creationTime = new Date(ticketData.createdAt).getTime();
          const currentTime = new Date().getTime();
          const timeSinceCreation = currentTime - creationTime;

          if (timeSinceCreation >= 10000) {
            setShowDownload(true);
          } else {
            const timeToWait = 10000 - timeSinceCreation;
            pollingTimeout = setTimeout(() => {
              if (isMounted) {
                setShowDownload(true);
              }
            }, timeToWait);
          }

          // Stop polling - payment confirmed and PDF is ready
          return;
        }

        // Not ready yet, continue polling
        pollingTimeout = setTimeout(() => pollTicket(currentTicketId), 3000);

      } catch (error) {
        if (!isMounted) return;
        console.error("Error during polling:", error);
        setError(t("Failed to load ticket. Please refresh the page or contact support."));
        setIsLoadingTicket(false);
      }
    };

    // Start polling with the ticketId we have
    if (ticketId) {
      pollTicket(ticketId);
    } else if (sessionId) {
      // If we only have sessionId, we need to resolve it to a ticketId first
      // For now, show error - this case should be handled by Stripe redirect
      setError(t("Processing payment..."));
    }

    // Cleanup
    return () => {
      isMounted = false;
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
    };
  }, [ticketId, sessionId, t, isNonStripeFlow, orderId]);

  // Track window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate QR code when ticket is available on mobile
  useEffect(() => {
    if (ticket?.id && isMobile) {
      QRCode.toDataURL(shortUUID().fromUUID(ticket.id), { width: 200, margin: 1 })
        .then(setQrImageUrl);
    }
  }, [ticket?.id, isMobile]);

  // Invoice polling effect — starts after ticket PDF is ready.
  // Skipped for invoice flow: no Stripe-side invoice is generated; accounting handles it manually.
  useEffect(() => {
    if (!orderId || !isPDFReady || !paymentVerified || skipInvoicePolling) return;

    let isMounted = true;
    let pollingTimeout = null;
    let attempts = 0;
    const maxAttempts = 30; // ~90 seconds at 3s intervals

    setIsLoadingInvoice(true);

    const pollInvoice = async () => {
      if (!isMounted || attempts >= maxAttempts) {
        if (isMounted) setIsLoadingInvoice(false);
        return;
      }

      attempts++;
      const invoice = await fetchInvoice(orderId);

      if (!isMounted) return;

      if (invoice) {
        setInvoiceData(invoice);
        setIsLoadingInvoice(false);
        return;
      }

      // Not ready yet, continue polling
      pollingTimeout = setTimeout(pollInvoice, 3000);
    };

    pollInvoice();

    return () => {
      isMounted = false;
      if (pollingTimeout) clearTimeout(pollingTimeout);
    };
  }, [orderId, isPDFReady, paymentVerified, skipInvoicePolling]);

  // Error state
  if (error) {
    return (
      <div className="container mt-4">
        <MDBCard className="mx-auto" style={{ maxWidth: '600px' }}>
          <MDBCardBody className="text-center py-5">
            <MDBIcon fas icon="exclamation-triangle" size="4x" className="text-warning mb-3" />
            <MDBTypography tag="h4" className="mt-3">
              {error}
            </MDBTypography>
            <MDBBtn color="primary" className="mt-3" href="/bookings">
              {t("Go to My Tickets")}
            </MDBBtn>
          </MDBCardBody>
        </MDBCard>
      </div>
    );
  }

  // Payment failed or timed out
  if (paymentFailed) {
    return (
      <div className="container mt-4">
        <MDBCard className="mx-auto" style={{ maxWidth: '600px' }}>
          <MDBCardBody className="text-center py-5">
            <MDBIcon fas icon="times-circle" size="4x" className="text-danger mb-3" />
            <MDBTypography tag="h4" className="mt-3">
              {t("Payment could not be confirmed")}
            </MDBTypography>
            <p className="text-muted mt-2">
              {t("Your payment could not be verified. Please check your tickets or contact support.")}
            </p>
            <MDBBtn color="primary" className="mt-3" href="/bookings">
              {t("Go to My Tickets")}
            </MDBBtn>
          </MDBCardBody>
        </MDBCard>
      </div>
    );
  }

  // Loading ticket data
  if (isLoadingTicket && !ticket) {
    return (
      <div className="container mt-4">
        <MDBCard className="mx-auto" style={{ maxWidth: '600px' }}>
          <MDBCardBody className="text-center py-5">
            <MDBSpinner color="primary" style={{ width: '4rem', height: '4rem' }} />
            <MDBTypography tag="h4" className="mt-4">
              {t("Loading ticket...")}
            </MDBTypography>
            <p className="text-muted mt-2">
              {t("Please wait while we retrieve your ticket information.")}
            </p>
          </MDBCardBody>
        </MDBCard>
      </div>
    );
  }

  // Waiting for payment verification
  if (isLoadingTicket && ticket && !paymentVerified) {
    return (
      <div className="container mt-4">
        <MDBCard className="mx-auto" style={{ maxWidth: '600px' }}>
          <MDBCardBody className="text-center py-5">
            <MDBSpinner color="primary" style={{ width: '4rem', height: '4rem' }} />
            <MDBTypography tag="h4" className="mt-4">
              {t("Verifying payment...")}
            </MDBTypography>
            <p className="text-muted mt-2">
              {t("Please wait while we confirm your payment.")}
            </p>
          </MDBCardBody>
        </MDBCard>
      </div>
    );
  }

  // Waiting for PDF generation (payment verified, but PDF not ready)
  if (isLoadingTicket && ticket && paymentVerified && !isPDFReady) {
    return (
      <div className="container mt-4">
        <MDBCard className="mx-auto" style={{ maxWidth: '600px' }}>
          <MDBCardBody className="text-center py-5">
            <MDBSpinner color="success" style={{ width: '4rem', height: '4rem' }} />
            <MDBTypography tag="h4" className="mt-4 text-success">
              {t("Booking Successful!")}
            </MDBTypography>
            <p className="mt-3 fw-bold text-primary">{t("Generating your ticket PDF...")}</p>
            <p className="text-primary">
              {t("This usually takes 10-30 seconds. Please don't close this page.")}
            </p>
          </MDBCardBody>
        </MDBCard>
      </div>
    );
  }

  // Ticket ready - show details and download buttons
  if (!ticket) {
    return (
      <div className="container mt-4">
        <MDBCard className="mx-auto" style={{ maxWidth: '600px' }}>
          <MDBCardBody className="text-center py-5">
            <MDBSpinner color="primary" style={{ width: '4rem', height: '4rem' }} />
            <MDBTypography tag="h4" className="mt-4">
              {t("Loading...")}
            </MDBTypography>
          </MDBCardBody>
        </MDBCard>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <MDBTypography tag="h1" className="text-center text-success mb-4">
        <MDBIcon fas icon="check-circle" className="me-2" />
        {t("Booking Successful!")}
      </MDBTypography>

      {skipInvoicePolling && (
        <div className="alert alert-info mx-auto" style={{ maxWidth: '800px' }} role="alert">
          <MDBIcon
            fas
            icon={event?.paymentFlow === "review_then_pay" ? "hourglass-start" : "file-invoice"}
            className="me-2"
          />
          {event?.paymentFlow === "review_then_pay"
            ? t("Your order is being reviewed. You will receive an email with payment instructions once approved.")
            : t("Your booking is confirmed. The invoice will be sent to you separately by our accounting team.")}
        </div>
      )}

      {isMobile && qrImageUrl && ticket?.personalAddress && (
        <MDBCard className="mx-auto mb-3" style={{ maxWidth: '800px' }}>
          <MDBCardBody className="text-center">
            <MDBCardTitle>{t("Your Ticket QR Code")}</MDBCardTitle>
            <img src={qrImageUrl} alt="Ticket QR Code" style={{ width: 200, height: 200 }} />
            <p className="mt-2 mb-0 fw-bold">
              {ticket.personalAddress.firstName} {ticket.personalAddress.lastName}
            </p>
          </MDBCardBody>
        </MDBCard>
      )}

      <MDBCard className="mx-auto" style={{ maxWidth: '800px' }}>
        <MDBCardBody>
          <MDBCardTitle>{t("Your Ticket Details")}</MDBCardTitle>

          <div className="mb-4">
            <h5 className="text-primary">{t("Event")}</h5>
            <p>{ticket.event.name.find(n => n.language === i18n.language)?.text || ticket.event?.name[0].text}</p>

            <h5 className="text-primary mt-3">{t("Selected Days")}</h5>
            <p>{ticket.eventDays.map(day => isoToGermanDate(day)).join(", ")}</p>

            <h5 className="text-primary mt-3">{t("Booking Date")}</h5>
            <p>{isoToGermanDate(ticket.createdAt)}</p>
          </div>

          <div className="mb-4">
            <h5 className="text-primary">{t("Download Ticket")}</h5>

            {isPDFReady && showDownload && ticket?.event && ticket?.personalAddress ? (
              <MDBBtnGroup shadow='0' aria-label='Download options' className="w-100">
                {ticket?.event?.image?.fileName && ticket?.id && ticket?.personalAddress?.firstName && ticket?.personalAddress?.lastName && (
                  <PDFDownloadLink
                    document={
                      <MobileTicketPDF
                        eventImageUrl={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/EventImages/${ticket.event.image.fileName}`}
                        qrCodeValue={shortUUID().fromUUID(ticket.id)}
                        ticketOwnerName={`${ticket.personalAddress.firstName} ${ticket.personalAddress.lastName}`}
                        selectedDays={ticket.eventDays.map(isoToGermanDate)}
                        event={ticket.event}
                        ticket={ticket}
                      />
                    }
                    fileName={`Mobile_Ticket_${ticket.personalAddress.firstName}_${ticket.personalAddress.lastName}_&_${getLocalizedText(ticket.event.name)}.pdf`}
                    className="btn ripple ripple-surface ripple-surface-dark btn btn-outline-secondary"
                  >
                    {({ loading }) =>
                      loading ? (
                        <>
                          <MDBIcon fas size='2x' icon="spinner" spin />
                        </>
                      ) : (
                        <>
                          <MDBIcon fas size='2x' icon="mobile" />
                          <br />
                          {t("Mobile Ticket")}
                        </>
                      )
                    }
                  </PDFDownloadLink>
                )}

                {ticket?.id && (
                  <a
                    className="btn ripple ripple-surface ripple-surface-dark btn btn-outline-secondary"
                    href={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/tickets/${ticket.id}.pdf`}
                    target="_blank"
                    rel="noreferrer"
                    download
                  >
                    <MDBIcon fas size='2x' icon="file-pdf" />
                    <br />
                    {t("A4 PDF Ticket")}
                  </a>
                )}
              </MDBBtnGroup>
            ) : (
              <div className="w-100 text-center py-3">
                <MDBSpinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                <p className="mt-3 fw-bold">
                  {!isPDFReady ? t("Generating your ticket PDF...") : t("Preparing download...")}
                </p>
                <p className="text-muted small">
                  {!isPDFReady
                    ? t("This usually takes 10-30 seconds.")
                    : t("Download will be available shortly.")}
                </p>
              </div>
            )}
          </div>

          {/* Invoice section — only for paid tickets */}
          {orderId && (
            <div className="mb-4">
              <h5 className="text-primary">{t("Invoice")}</h5>
              {invoiceData?.s3Path ? (
                <a
                  className="btn btn-outline-secondary"
                  href={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/${invoiceData.s3Path}`}
                  target="_blank"
                  rel="noreferrer"
                  download
                >
                  <MDBIcon fas icon="file-invoice" className="me-2" />
                  {t("Download Invoice")} ({invoiceData.documentNumber})
                </a>
              ) : isLoadingInvoice ? (
                <div className="d-flex align-items-center">
                  <MDBSpinner size="sm" color="primary" className="me-2" />
                  <span className="text-muted">{t("Generating invoice...")}</span>
                </div>
              ) : (
                <p className="text-muted">
                  {t("You will receive the invoice by email.")}
                </p>
              )}
            </div>
          )}
        </MDBCardBody>
      </MDBCard>
    </div>
  );
}
